// Local-timezone formatting for chat timestamps.
//
// The backend stores timestamps in UTC (Postgres timestamptz -> ISO string with
// a trailing Z). Constructing `new Date(utcString)` yields the correct instant,
// and the `toLocale*` methods render it in the viewer's own local timezone with
// no extra configuration — exactly what we want for agents in any timezone.

const toDate = (ts?: string | number | Date | null): Date | null => {
  if (ts === undefined || ts === null || ts === '') return null;
  const d = ts instanceof Date ? ts : new Date(ts);
  return isNaN(d.getTime()) ? null : d;
};

/** Local time of day, e.g. "2:32 PM" (locale-dependent). */
export const formatMessageTime = (ts?: string | number | Date | null): string => {
  const d = toDate(ts);
  return d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
};

/** Friendly local date for a day separator: "Today" / "Yesterday" / "14 Jun 2026". */
export const formatMessageDate = (ts?: string | number | Date | null): string => {
  const d = toDate(ts);
  if (!d) return '';
  const now = new Date();
  const yesterday = new Date();
  yesterday.setDate(now.getDate() - 1);
  const sameDay = (a: Date, b: Date) =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();
  if (sameDay(d, now)) return 'Today';
  if (sameDay(d, yesterday)) return 'Yesterday';
  return d.toLocaleDateString([], { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Full local date + time, used for hover tooltips. */
export const formatMessageDateTime = (ts?: string | number | Date | null): string => {
  const d = toDate(ts);
  return d ? d.toLocaleString() : '';
};

/** Stable per-day key in LOCAL time (YYYY-MM-DD), for grouping messages by day. */
export const localDayKey = (ts?: string | number | Date | null): string => {
  const d = toDate(ts);
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};
